import { action, computed, thunk, thunkOn } from 'easy-peasy';
import { api } from '~/api';
import { history } from '~/history';
import { toast } from 'react-toastify';
import * as tenderEvents from '~/services/tenders/events';
import { getSentryInstance } from '~/sentry';
import { defaultSetting } from './defaultSetting';
import {
  extractSkinFromPath,
  findSolutionBySkin,
  normalizeSkin,
  shouldExcludeFromSkinRedirect,
} from '~/router/solutionUrl';

export const authModel = {
  user: null,
  solutionId: null,
  loading: true, // indicates token loading & sign-in, default to true to show loading spin before routing.
  routes: [],
  participantToast: null,

  // Selectors
  userModel: computed((state: any) => state.user),
  solutions: computed((state: any) => state.user?.solutions ?? []),
  solution: computed((state: any) =>
    state.solutions.find((s: any) => s.id === state.solutionId)
  ),
  solutionColumns: computed((state: any) => state.solution?.columns ?? {}),
  isdao: computed(
    (state: any) =>
      state.solution &&
      state.solution.id === 'c429cff0-a32a-40b2-9243-71547ae65a3a'
  ),

  solutionDivisions: computed((state: any) =>
    (state.solution?.roles ?? []).map((role: any) => role.division)
  ),

  solutionDivisionIds: computed((state: any) =>
    (state.solution?.roles ?? []).map((role: any) => role.division?.id)
  ),

  solutionWarehouses: computed((state: any) =>
    (state.solution?.roles ?? []).flatMap(
      (role: any) => role.division?.warehouses ?? []
    )
  ),

  canTradeDivisions: computed((state: any) =>
    (state.solution?.roles ?? [])
      .filter((role: any) => ['manager', 'trader'].includes(role.role))
      .map((role: any) => role.division)
  ),

  canManageDivisions: computed((state: any) =>
    (state.solution?.roles ?? [])
      .filter((role: any) => role.role === 'manager')
      .map((role: any) => role.division)
  ),

  canManageProductsDivisions: computed((state: any) =>
    (state.solution?.roles ?? [])
      .filter((role: any) => role.division?.manageproducts)
      .map((role: any) => role.division)
  ),

  userTradeActions: computed((state: any) => {
    let canBuy = false;
    let canSell = false;

    const actions: string[] = [];
    state.canTradeDivisions.forEach((item: any) => {
      if (
        !canBuy &&
        ['buyer', 'trader', 'broker'].includes(item.divisiontype)
      ) {
        canBuy = true;
        actions.push('buy');
      }
      if (
        !canSell &&
        ['seller', 'trader', 'broker'].includes(item.divisiontype)
      ) {
        canSell = true;
        actions.push('sell');
      }
    });

    return actions;
  }),

  hasRole: computed(
    (state: any) =>
      (role: any, solution = state.solution) =>
        (solution?.roles ?? []).map((r: any) => r.role).includes(role)
  ),

  hasRoleInAnySolution: computed(
    (state: any) =>
      (role: any, solutions = state.solutions) =>
        solutions.some((solution: any) =>
          (solution?.roles ?? []).map((r: any) => r.role).includes(role)
        )
  ),

  privacyCheckReq: computed((state: any) => {
    const { user, solution } = state;

    if (!user || !solution) return false;

    if (!solution.policies || solution.policies.length === 0) return false;

    return solution.policies.some((p: any) => p.accepted === false);
  }),

  isSolutionOwner: computed(
    (state: any) => state.solution?.owner?.id === state.user?.company?.id
  ),

  canEditExchangeRates: computed(
    (state: any) =>
      (state.solution?.settings?.manualexchange ?? false) &&
      state.isSolutionOwner &&
      state.hasRole('manager')
  ),

  traderType: computed((state: any) => {
    let [seller, buyer] = [false, false];
    for (const r of state.solution?.roles ?? []) {
      if (r.division?.seller) seller = true;
      if (r.division?.buyer) buyer = true;
      if (seller && buyer) break;
    }
    return { seller, buyer };
  }),

  userPrefs: computed((state: any) => state.user?.prefs ?? {}),

  solutionSettings: computed((state: any) => {
    const solution = state.solutions.find(
      (s: any) => s.id === state.solutionId
    );
    return {
      ...defaultSetting,
      ...solution?.settings,
      hasPartners: !!(solution?.columns?.buyer || solution?.columns?.consignee),
    };
  }),

  // Actions
  signIn: action((state: any, { user, solutionId }) => {
    state.user = user;
    if (solutionId) {
      state.solutionId = solutionId;
    }
  }),
  signOut: action((state: any) => {
    state.user = null;
  }),
  setLoading: action((state: any, loading) => {
    state.loading = loading;
  }),
  changeSolution: action((state: any, id) => {
    // Blank the content area (PageWrapper shows only <TopNav/>) until the new
    // solution's logo is loaded — cleared in App.tsx once the image is cached
    // so the header doesn't flash the previous logo against the new colours.
    state.loading = true;
    state.solutionId = id;
  }),
  // Get solution by skin identifier
  getSolutionBySkin: computed(
    (state: any) => (skin: any) =>
      state.solutions.find((s: any) => s.skin === skin)
  ),
  updateRoutes: action((state: any, routes) => {
    state.routes = routes
      .map((item: any) => ({
        key: `${item.source.id}-${item.destination.id}`,
        ...item,
      }))
      .sort((a: any, b: any) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  }),
  updateUser: action((state: any, data) => {
    state.user = { ...state.user, ...data };
  }),
  setParticipantToast: action((state: any, data) => {
    state.participantToast = data;
  }),
  dismissParticipants: action((state: any) => {
    const s = state.solutions.find((sol: any) => sol.id === state.solution.id);
    state.user.solutions = [
      ...state.solutions.filter((sol: any) => sol.id !== state.solution.id),
      { ...s, new_participants: 0 },
    ];
  }),
  updateFavouriteproducts: action((state: any, favouriteproducts) => {
    const s = state.solutions.find((sol: any) => sol.id === state.solution.id);
    state.user.solutions = [
      ...state.solutions.filter((sol: any) => sol.id !== state.solution.id),
      { ...s, favouriteproducts },
    ];
  }),

  // Thunks
  loadUserProfile: thunk(async (actions: any) => {
    const result = await api.getData({
      type: 'user',
      id: `profile`,
      getResult: (x: any) => x,
    });
    actions.updateUser(result);
  }),

  loadUserSolutions: thunk(async (actions: any) => {
    const result = await api.getData({
      type: 'user',
    });
    actions.updateUser({
      solutions: result.solutions,
    });
  }),

  // Start token exchange cycle by using provided code
  checkAuth: thunk(
    async (
      actions: any,
      payload: any,
      { injections: { api, authService, history } }
    ) => {
      let user;
      try {
        // from signin
        const target = payload?.target ?? {};
        const camefrom = payload?.camefrom ?? '/';
        const redirect = payload?.redirect ?? '/';
        let token = payload?.token ?? null;
        const urlSolution = payload?.urlSolution ?? '';
        // Extract skin directly from URL
        const urlSkin =
          extractSkinFromPath(window.location.pathname).skin || '';

        if (!token) {
          // Don't clearToken() here — saveToken() below overwrites __t,
          // and clearing pre-emptively opens a race where in-flight
          // request()/Socket reads see no token and trigger TokenError
          // (FRAICHE-5Q).
          token = await api.refresh();
        }

        api.updateToken(token);
        authService.saveToken(token);

        let sid = target.solution || urlSolution || authService.loadSolution();

        // check for solution override
        const UUID =
          '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

        const tradeRegex = new RegExp(`^/trades/(${UUID})$`);
        const previewTradeRegex = new RegExp(`^/preview/trade/(${UUID})$`);
        const tenderRegex = new RegExp(
          `^/tenders/(${UUID})(?:/orders/[0-9a-f-]+/details)?$`
        );

        const location = window.location;
        const pathName = location.pathname;

        const urlParams = new URLSearchParams(window.location.search);
        const orderParamId = urlParams.get('order');
        let orderPid = null;

        if (orderParamId) {
          try {
            const data = await api.getData({
              type: 'orders',
              id: orderParamId,
            });
            sid = data.solution.id;
            // Defer redirect until we know the solution's skin
            orderPid = data.pid;
          } catch {
            // redirect to home - preserve skin prefix
            const skinPrefix = urlSkin ? `/${urlSkin}` : '';
            history.replace(`${skinPrefix}/`);
          }
        } else if (tradeRegex.test(pathName)) {
          try {
            const data = await api.getData({
              type: 'trades',
              id: pathName.match(tradeRegex)?.[1],
            });
            sid = data.solution.id;
          } catch (e: any) {
            if (e?.response?.status !== 403) throw e;
          }
        } else if (previewTradeRegex.test(pathName)) {
          try {
            const data = await api.getData({
              type: 'trades',
              id: pathName.match(previewTradeRegex)?.[1],
            });
            sid = data.solution.id;
          } catch (e: any) {
            if (e?.response?.status !== 403) throw e;
          }
        } else if (tenderRegex.test(pathName)) {
          try {
            const data = await api.getData({
              type: 'tenders',
              id: pathName.match(tenderRegex)?.[1],
            });
            sid = data.solution.id;
          } catch (e: any) {
            if (e?.response?.status !== 403) throw e;
          }
        }

        // If URL has a skin, we need to verify we have the right solution
        // If sid is from localStorage and doesn't match the skin, clear it to fetch fresh
        if (urlSkin && sid === authService.loadSolution()) {
          // sid is from localStorage, not from order/trade/tender override
          // Clear it so we fetch the full solutions list and can find by skin
          sid = null;
        }

        if (!sid) {
          // Fetch solutions to find the right one
          const res = await api.getDataV2Api(`/user`);
          const allSolutions = res?.user?.solutions || [];
          // If urlSkin provided, find solution by skin; otherwise use first
          const skinSolution = urlSkin
            ? findSolutionBySkin(allSolutions, urlSkin)
            : null;
          sid = skinSolution?.id || allSolutions[0]?.id;
        }

        user = await api.getData({ type: 'user' }, { solution: sid });

        // if we are getting user with empty solutions, we need to get user again
        if (user.solutions.length < 1) {
          const res = await api.getDataV2Api(`/user`);
          sid = res?.user?.solutions?.[0]?.id;
          user = await api.getData({ type: 'user' }, { solution: sid });
        }

        // Determine solution ID before signIn to avoid flash
        const solutions = user.solutions || [];
        const solutionFromSkin = urlSkin
          ? findSolutionBySkin(solutions, urlSkin)
          : null;
        const finalSid = [
          target.solution,
          // sid resolved from ?order=, /trades/, /tenders/ — must win
          // over skin/url/stored fallbacks so the deep-linked resource's
          // solution is the one we sign the user into.
          sid,
          solutionFromSkin?.id,
          urlSolution,
          authService.loadSolution(),
          solutions[0]?.id,
        ].find((id: any) => solutions.find((s: any) => s.id === id));

        // Deferred redirect for ?order= — use the order's solution skin
        if (orderPid) {
          const orderSolution = solutions.find((s: any) => s.id === sid);
          const targetSkin = normalizeSkin(orderSolution?.skin) || urlSkin;
          const skinPrefix = targetSkin ? `/${targetSkin}` : '';
          history.replace(
            `${skinPrefix}?${new URLSearchParams({
              ...Object.fromEntries(urlParams.entries()),
              order: orderPid,
            })}`
          );
        }

        actions.signIn({
          user,
          solutionId: finalSid,
          target,
          camefrom,
          urlSolution,
          redirect,
        });
      } catch (e: any) {
        // only sign user out if 401 unauthorized
        // api.refresh() throws an enhanced Error with .status/.isAuthError and
        // no .response, so check both shapes here (NUI-4576).
        if (
          e?.response?.status === 401 ||
          e?.status === 401 ||
          e?.isAuthError
        ) {
          actions.setLoading(false);
          if (window.location.pathname !== '/signin') {
            actions.signOut({ preserveUrl: true });
          }
          return;
        }
        if ((user?.solutions?.length ?? 0) < 1) {
          throw e;
        }
        // To has an error display in render, we need to set up another variable for it in global store
        // message.error('Sign in failed. Please try again');
      }
      actions.setLoading(false);
    }
  ),

  fetchRoutes: thunk(
    async (actions: any, solutionId: any, { injections: { api } }) => {
      try {
        const params = { solution: solutionId };
        const routes = await api.getData2('/routes', params);
        actions.updateRoutes(routes.routes);
      } catch {
        // nothing
      }
    }
  ),

  fetchUser: thunk(async (actions: any, _: any, { injections: { api } }) => {
    try {
      const user = await api.getData({ type: 'user' });
      actions.updateUser(user);
    } catch {
      // nothing
    }
  }),

  onSignIn: thunkOn(
    (actions: any) => actions.signIn,
    async (
      actions: any,
      { payload: { user, target, camefrom, urlSolution, redirect } }: any,
      { injections: { authService }, getStoreActions }: any
    ) => {
      getStoreActions().auth.loadUserSolutions();
      const solutions = user?.solutions ?? [];

      // Extract skin from URL and look up solution
      const urlSkin = extractSkinFromPath(window.location.pathname).skin;
      const solutionFromSkin = urlSkin
        ? findSolutionBySkin(solutions, urlSkin)
        : null;

      /**
       * Set solution to be either:
       * 1. passed-in one through /authenticate
       * 2. solution from URL skin (e.g., /hilmar/orders)
       * 3. ?solution= param in URL
       * 4. persistent one in localStorage
       * 5. first one of user.solutions
       */
      const sid = [
        target.solution,
        solutionFromSkin?.id,
        urlSolution,
        authService.loadSolution(),
        solutions[0]?.id,
      ].find((id: any) => solutions.find((s: any) => s.id === id));
      actions.changeSolution(sid);
      /**
       * TODO test scenarios for input URLs with user signed in or not.
       * - when prev token works, redirect to origin URL thru routing
       * - Otherwise, ideally we want to reach the origin URL
       */

      if (target.type) {
        try {
          switch (target.type) {
            case 'order':
              history.replace(`/?order=${target.id}`);
              return;

            case 'trade':
              history.replace(`/trades/${target.id}`);
              return;

            case 'participants':
              history.replace(
                `/participants?activatedon=gte:${target.activatedon}`
              );
              return;

            // Deep link path stored on the token. Guard against an
            // absolute/protocol-relative path so a poisoned token can't bounce
            // the user off-site.
            case 'url':
              if (target.path?.startsWith('/') && !target.path.startsWith('//'))
                history.replace(target.path);
              return;

            default:
              break;
          }
        } catch {
          // nothing to worry about
        }
      }
      if (redirect !== false) {
        const urlParams = new URLSearchParams(window.location.search);
        const stayOnRoot =
          urlParams.has('solution') ||
          urlParams.has('order') ||
          target.solution;
        // Prioritize camefrom when it's a specific path (not root)
        if (camefrom && camefrom !== '/') {
          history.replace(camefrom);
        } else if (stayOnRoot) {
          // checkAuth may have added a skin prefix and ?order=/?solution= must
          // survive — but a login/signin path is not a valid landing URL
          // (e.g. admin magic-link), so build the root fresh from the chosen
          // solution's skin to avoid redirecting back onto the login page.
          if (shouldExcludeFromSkinRedirect(window.location.pathname)) {
            const chosenSolution = solutions.find((s: any) => s.id === sid);
            const skin = normalizeSkin(chosenSolution?.skin);
            history.replace(skin ? `/${skin}` : '/');
          } else {
            history.replace(window.location.pathname + window.location.search);
          }
        } else if (user.landingpage_access !== false) {
          history.replace('/');
        } else {
          // No landing page access — go to their solution home
          const chosenSolution = solutions.find((s: any) => s.id === sid);
          const skin = normalizeSkin(chosenSolution?.skin);
          history.replace(skin ? `/${skin}` : '/');
        }
      }
    }
  ),
  onSignOut: thunkOn(
    (actions: any) => actions.signOut,

    async (
      actions: any,
      { payload }: any,
      { injections: { api, authService } }: any
    ) => {
      actions.setLoading(true);
      // Clear localStorage token immediately to prevent race condition if user navigates quickly
      // Keep api.token for the signOut call to authenticate
      authService.clearToken();
      tenderEvents.signout({});
      actions.changeSolution(null);
      // Destroy the cookie/session on server
      try {
        await api.signOut();
      } catch {
        // Nothing
      }
      api.updateToken(null);

      // Directly redirect to root
      // TODO Ensure it works for different scenarios
      if (payload?.preserveUrl) {
        history.replace('/signin#signout', {
          from: window.location.pathname + window.location.search,
        });
      } else {
        history.replace('/signin#signout');
      }
      actions.setLoading(false);
    }
  ),
  onChangeSolution: thunkOn(
    (actions: any) => actions.changeSolution,
    async (
      actions: any,
      { payload: id }: any,
      { injections: { authService }, getState, getStoreActions }: any
    ) => {
      const user = getState().user;

      getStoreActions().marketplace.reset();
      toast.dismiss(getState().participantToast);
      if (id) {
        authService.saveSolution(id);
        tenderEvents.solution({ solution: id });

        const sentry = getSentryInstance();
        if (sentry) {
          sentry.setUser({
            id: user.id,
            email: user.email,
            solution: id,
            company: user.company?.name,
          });
          sentry.setTag('company_id', user.company?.id);
          sentry.setTag('company_name', user.company?.name);
          const canTradeDivisions = getState().canTradeDivisions;
          const primaryDivision = canTradeDivisions?.[0];
          if (primaryDivision) {
            sentry.setTag('division_id', primaryDivision.id);
            sentry.setTag('division_name', primaryDivision.name);
            sentry.setTag('division_type', primaryDivision.divisiontype);
          }
        }
      } else {
        authService.clearSolution(id);
      }
    }
  ),
};
